import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form } from '@douyinfe/semi-ui';
import { IconSearch, IconEdit, IconDelete, IconPlus } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import UniImage from '../../components/UniImage';
import UserSelector from '../../components/UserSelector';
import VipCardSelector from '../../components/VipCardSelector';
import { useSearchParams } from 'react-router-dom';

const UserVipCards = () => {
    const [searchParams] = useSearchParams();
    const urlCardUid = searchParams.get('card_uid');
    const urlSuUid = searchParams.get('su_uid');

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [searchCardUid, setSearchCardUid] = useState(urlCardUid || null);
    const [searchSuUid, setSearchSuUid] = useState(urlSuUid || null);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/vip/list_user_vip/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    card_uid: searchCardUid || '',
                    su_uid: searchSuUid || ''
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/vip/add_user_vip/', {
                su_uid: values.su_uid,
                card_uid: values.card_uid,
            });
            if (response.errno === 0) {
                Toast.success('开卡成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '开卡失败');
            }
        } catch (error) {
            console.error('开卡失败:', error);
            Toast.error('开卡失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp || (timestamp == "0")) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '用户信息',
            dataIndex: 'su',
            width: 200,
            render: (su) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {su?.avatar && <UniImage src={su.avatar} width={32} height={32} style={{ borderRadius: '50%' }} />}
                    <div>
                        <div>{su?.name || '-'}</div>
                    </div>
                </div>
            )
        },
        {
            title: '会员卡信息',
            dataIndex: 'card',
            width: 200,
            render: (card) => card?.name || '-'
        },
        {
            title: '卡类型',
            dataIndex: 'typ',
            width: 100,
            render: (typ) => {
                const typeMap = {
                    1: '储值卡',
                    2: '次卡',
                    4: '折扣卡'
                };
                return typeMap[typ] || '-';
            }
        },
        {
            title: '余额信息',
            width: 200,
            render: (_, record) => {
                if (record.typ & 1) {
                    return (
                        <div>
                            <div>剩余：¥{(record.cash_remain / 100).toFixed(2)}</div>
                            <div>已用：¥{(record.cash_used / 100).toFixed(2)}</div>
                        </div>
                    );
                }
                return '-';
            }
        },
        {
            title: '次数信息',
            width: 150,
            render: (_, record) => {
                if (record.typ & 2) {
                    return (
                        <div>
                            <div>剩余：{record.cnt_remain}次</div>
                            <div>已用：{record.cnt_used}次</div>
                        </div>
                    );
                }
                return '-';
            }
        },
        {
            title: '折扣率',
            dataIndex: 'discount_rate',
            width: 100,
            render: (text, record) => {
                if (record.typ & 4) {
                    return text ? `${text}%` : '-';
                }
                return '-';
            }
        },
        {
            title: '有效期',
            dataIndex: 'expire_time',
            width: 180,
            render: (text) => formatTimestamp(text)
        },
        {
            title: '创建/更新时间',
            width: 180,
            render: (_, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(record.update_time)}</div>
                </div>
            )
        }
    ];

    const renderForm = () => {
        return (
            <Form
                getFormApi={setFormApi}
                onSubmit={handleAdd}
                style={{ padding: '20px' }}
            >
                <Space vertical style={{ width: '100%' }}>
                    <UserSelector
                        field="su_uid"
                        label="选择用户"
                        rules={[{ required: true, message: '请选择用户' }]}
                        style={{ width: '100%' }}
                        noLabel
                        multiple={false}
                        onChange={(value) => formApi?.setValue('su_uid', value)}
                    />

                    <VipCardSelector
                        field="card_uid"
                        label="选择会员卡"
                        rules={[{ required: true, message: '请选择会员卡' }]}
                        style={{ width: '100%' }}
                        noLabel
                        multiple={false}
                        onChange={(value) => formApi?.setValue('card_uid', value)}
                    />
                </Space>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.userVipCardsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索用户名称/手机号"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <UserSelector
                        onChange={setSearchSuUid}
                        style={{ width: 200 }}
                        placeholder="选择用户筛选"
                        noLabel
                        multiple={false}
                    />
                    <VipCardSelector
                        onChange={setSearchCardUid}
                        style={{ width: 200 }}
                        placeholder="选择会员卡筛选"
                        noLabel
                        multiple={false}
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>开通会员卡</Button>
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
            />

            <Modal
                title="开通会员卡"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {renderForm()}
            </Modal>
        </div>
    );
};

export default UserVipCards;